import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import './NotificacionesPage.css';
import { API_BASE_URL } from './config';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const estadoFilters = [
  { value: 'pendiente', label: 'Pendientes' },
  { value: 'atendida', label: 'Atendidas' },
  { value: 'todas', label: 'Todas' }
];

const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('es-MX', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatFileSize = (bytes) => {
  const size = Number(bytes);
  if (!Number.isFinite(size) || size < 1024) return `${size || 0} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const getFileExtension = (fileName = '') => {
  const normalized = String(fileName || '').trim();
  if (!normalized.includes('.')) return 'FILE';
  const parts = normalized.split('.');
  const extension = parts[parts.length - 1] || '';
  return extension.toUpperCase().slice(0, 5) || 'FILE';
};

const normalizeDisplayFileName = (value = '') => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  try {
    const decoded = decodeURIComponent(escape(raw));
    if (/[ÃÂÐÑ]/.test(raw) && /[áéíóúÁÉÍÓÚñÑüÜ]/.test(decoded)) {
      return decoded;
    }
  } catch (_error) {
    // Ignore decoding error and keep raw value.
  }
  return raw;
};

const isImageAttachment = (adjunto = {}) => {
  const mime = String(adjunto.mime_type || '').toLowerCase();
  const ext = getFileExtension(adjunto.file_name).toLowerCase();
  return mime.startsWith('image/') || ['jpg', 'jpeg', 'jpge', 'png'].includes(ext);
};

const isPdfAttachment = (adjunto = {}) => {
  const mime = String(adjunto.mime_type || '').toLowerCase();
  const ext = getFileExtension(adjunto.file_name).toLowerCase();
  return mime === 'application/pdf' || ext === 'pdf';
};

function NotificacionesPage({ usuario, onPendingChange, refreshToken }) {
  const username = usuario?.username || '';
  const [usuarios, setUsuarios] = useState([]);
  const [destinatariosSeleccionados, setDestinatariosSeleccionados] = useState([]);
  const [mensaje, setMensaje] = useState('');
  const [archivosAdjuntos, setArchivosAdjuntos] = useState([]);
  const [notificaciones, setNotificaciones] = useState([]);
  const [sentNotifications, setSentNotifications] = useState([]);
  const [filtroEstado, setFiltroEstado] = useState('pendiente');
  const [loadingLista, setLoadingLista] = useState(false);
  const [loadingUsuarios, setLoadingUsuarios] = useState(false);
  const [loadingEnviadas, setLoadingEnviadas] = useState(false);
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', text: '' });
  const [previewAdjunto, setPreviewAdjunto] = useState(null);
  const [previewZoom, setPreviewZoom] = useState(1);
  const [pdfRenderScale, setPdfRenderScale] = useState(1);
  const [previewPan, setPreviewPan] = useState({ x: 0, y: 0 });
  const [isPreviewDragging, setIsPreviewDragging] = useState(false);
  const [pdfPages, setPdfPages] = useState(0);
  const [pdfCurrentPage, setPdfCurrentPage] = useState(1);
  const [pdfLoadError, setPdfLoadError] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const [focusScope, setFocusScope] = useState('');
  const [focusId, setFocusId] = useState(null);
  const inboxRefs = useRef({});
  const sentRefs = useRef({});
  const previewStageRef = useRef(null);
  const dragStartRef = useRef({
    startPointerX: 0,
    startPointerY: 0,
    startPanX: 0,
    startPanY: 0
  });

  const destinatariosDisponibles = useMemo(() => (
    usuarios.filter((user) => user.usuario !== username)
  ), [usuarios, username]);

  const cargarUsuarios = useCallback(async () => {
    setLoadingUsuarios(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/usuarios/listar`);
      const data = await response.json();
      if (Array.isArray(data)) {
        setUsuarios(data);
      }
    } catch (error) {
      console.error('No se pudieron cargar los usuarios:', error);
    } finally {
      setLoadingUsuarios(false);
    }
  }, []);

  const cargarNotificaciones = useCallback(async () => {
    if (!username) return;
    setLoadingLista(true);
    const params = new URLSearchParams({ usuario: username });
    if (filtroEstado !== 'todas') {
      params.append('estado', filtroEstado);
    }
    try {
      const response = await fetch(`${API_BASE_URL}/api/notificaciones?${params.toString()}`);
      if (!response.ok) {
        throw new Error('No se pudo obtener la bandeja');
      }
      const data = await response.json();
      const adaptadas = (Array.isArray(data) ? data : []).map((item) => ({
        id: item.destinatario_id,
        notificacionId: item.notificacion_id,
        remitente: item.remitente,
        mensaje: item.mensaje,
        creadoEn: item.creado_en,
        estado: item.estado,
        atendidoEn: item.atendido_en,
        adjuntos: Array.isArray(item.adjuntos) ? item.adjuntos : []
      }));
      setNotificaciones(adaptadas);
      if (typeof onPendingChange === 'function') {
        const pendientes = adaptadas.filter((item) => (
          item.estado !== 'atendida'
          && String(item?.remitente || '').toUpperCase() !== 'SISTEMA'
        )).length;
        onPendingChange(pendientes);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingLista(false);
    }
  }, [username, filtroEstado, onPendingChange]);

  const cargarEnviadas = useCallback(async () => {
    if (!username) return;
    setLoadingEnviadas(true);
    const params = new URLSearchParams({ remitente: username });
    try {
      const response = await fetch(`${API_BASE_URL}/api/notificaciones/enviadas?${params.toString()}`);
      if (!response.ok) {
        throw new Error('No se pudieron obtener las notificaciones enviadas');
      }
      const data = await response.json();
      const adaptadas = (Array.isArray(data) ? data : []).map((item) => ({
        id: item.id,
        mensaje: item.mensaje,
        creadoEn: item.creado_en,
        destinatarios: Array.isArray(item.destinatarios) ? item.destinatarios : [],
        pendientes: Array.isArray(item.pendientes) ? item.pendientes : [],
        atendidas: Number(item.atendidas) || 0,
        total: Number(item.total) || 0,
        adjuntos: Array.isArray(item.adjuntos) ? item.adjuntos : []
      }));
      setSentNotifications(adaptadas);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingEnviadas(false);
    }
  }, [username]);

  useEffect(() => {
    cargarUsuarios();
  }, [cargarUsuarios]);

  useEffect(() => {
    cargarNotificaciones();
    cargarEnviadas();
  }, [cargarNotificaciones, cargarEnviadas, refreshToken]);

  useEffect(() => {
    const scope = searchParams.get('focusScope');
    const idValue = Number(searchParams.get('focusId'));
    if ((scope === 'inbox' || scope === 'sent') && Number.isFinite(idValue)) {
      setFocusScope(scope);
      setFocusId(idValue);
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('focusScope');
      nextParams.delete('focusId');
      setSearchParams(nextParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (focusScope === 'inbox' && Number.isFinite(focusId)) {
      const target = inboxRefs.current[focusId];
      target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else if (focusScope === 'sent' && Number.isFinite(focusId)) {
      const target = sentRefs.current[focusId];
      target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [focusScope, focusId, notificaciones, sentNotifications]);

  useEffect(() => {
    if (!focusScope || !Number.isFinite(focusId)) return;
    const timeoutId = setTimeout(() => {
      setFocusScope('');
      setFocusId(null);
    }, 6000);
    return () => clearTimeout(timeoutId);
  }, [focusScope, focusId]);

  useEffect(() => {
    if (!previewAdjunto) return undefined;
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setPreviewAdjunto(null);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [previewAdjunto]);

  useEffect(() => {
    if (!previewAdjunto) return undefined;
    const previousBodyOverflow = document.body.style.overflow;
    const previousBodyTouchAction = document.body.style.touchAction;
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.body.style.touchAction = previousBodyTouchAction;
    };
  }, [previewAdjunto]);

  const toggleDestinatario = (usuarioDestino) => {
    setDestinatariosSeleccionados((prev) => {
      if (prev.includes(usuarioDestino)) {
        return prev.filter((value) => value !== usuarioDestino);
      }
      return [...prev, usuarioDestino];
    });
  };

  const toggleSeleccionTodos = () => {
    if (destinatariosSeleccionados.length === destinatariosDisponibles.length) {
      setDestinatariosSeleccionados([]);
    } else {
      setDestinatariosSeleccionados(destinatariosDisponibles.map((user) => user.usuario));
    }
  };

  const handleEnviar = async () => {
    setFeedback({ type: '', text: '' });
    if (!mensaje.trim()) {
      setFeedback({ type: 'error', text: 'Escribe un mensaje para poder enviarlo.' });
      return;
    }
    if (!destinatariosSeleccionados.length) {
      setFeedback({ type: 'error', text: 'Selecciona al menos un destinatario.' });
      return;
    }
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('remitente', username);
      formData.append('mensaje', mensaje.trim());
      formData.append('destinatarios', JSON.stringify(destinatariosSeleccionados));
      archivosAdjuntos.forEach((archivo) => {
        formData.append('adjuntos', archivo);
      });

      const response = await fetch(`${API_BASE_URL}/api/notificaciones`, {
        method: 'POST',
        body: formData
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.mensaje || 'No se pudo enviar la notificación.');
      }
      setMensaje('');
      setArchivosAdjuntos([]);
      setDestinatariosSeleccionados([]);
      setFeedback({ type: 'success', text: 'Notificación enviada con éxito.' });
      cargarNotificaciones();
      cargarEnviadas();
    } catch (error) {
      console.error(error);
      setFeedback({ type: 'error', text: error.message || 'Ocurrió un error al enviar la notificación.' });
    } finally {
      setSending(false);
    }
  };

  const handleAdjuntosChange = (event) => {
    const selected = Array.from(event.target.files || []);
    if (!selected.length) return;
    setArchivosAdjuntos((prev) => {
      const merged = [...prev, ...selected];
      if (merged.length > 10) {
        setFeedback({ type: 'error', text: 'Solo puedes adjuntar hasta 10 archivos por notificación.' });
      }
      return merged.slice(0, 10);
    });
    event.target.value = '';
  };

  const quitarAdjunto = (indexToRemove) => {
    setArchivosAdjuntos((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const buildAdjuntoUrl = (adjuntoId, mode = 'download') => (
    `${API_BASE_URL}/api/notificaciones/adjuntos/${adjuntoId}/${mode}`
  );

  const abrirPreviewAdjunto = (adjunto) => {
    if (!adjunto?.id) return;
    const displayName = normalizeDisplayFileName(adjunto.file_name);
    setPreviewAdjunto({
      id: adjunto.id,
      fileName: displayName,
      mimeType: adjunto.mime_type,
      isImage: isImageAttachment(adjunto),
      isPdf: isPdfAttachment(adjunto),
      viewUrl: buildAdjuntoUrl(adjunto.id, 'view'),
      downloadUrl: buildAdjuntoUrl(adjunto.id, 'download')
    });
    setPreviewZoom(1);
    setPdfRenderScale(1);
    setPreviewPan({ x: 0, y: 0 });
    setIsPreviewDragging(false);
    setPdfPages(0);
    setPdfCurrentPage(1);
    setPdfLoadError('');
  };

  const cerrarPreviewAdjunto = () => {
    setPreviewAdjunto(null);
    setPreviewZoom(1);
    setPdfRenderScale(1);
    setPreviewPan({ x: 0, y: 0 });
    setIsPreviewDragging(false);
    setPdfPages(0);
    setPdfCurrentPage(1);
    setPdfLoadError('');
  };

  const zoomInPreview = () => {
    setPreviewZoom((prev) => Math.min(prev + 0.25, 3));
  };

  const zoomOutPreview = () => {
    setPreviewZoom((prev) => Math.max(prev - 0.25, 0.5));
  };

  const resetPreviewZoom = () => {
    setPreviewZoom(1);
    setPdfRenderScale(1);
    setPreviewPan({ x: 0, y: 0 });
  };

  const handlePreviewWheel = (event) => {
    if (!previewAdjunto || (!previewAdjunto.isImage && !previewAdjunto.isPdf)) return;
    event.preventDefault();
    event.stopPropagation();

    const direction = event.deltaY < 0 ? 1 : -1;
    const step = 0.15;
    const oldZoom = previewZoom;
    const newZoom = Math.max(0.5, Math.min(3, oldZoom + (direction * step)));
    if (newZoom === oldZoom) return;

    if (previewAdjunto.isPdf) {
      setPreviewZoom(newZoom);
      return;
    }

    const stageRect = previewStageRef.current?.getBoundingClientRect();
    if (!stageRect) {
      setPreviewZoom(newZoom);
      return;
    }

    const cursorX = event.clientX - stageRect.left - (stageRect.width / 2);
    const cursorY = event.clientY - stageRect.top - (stageRect.height / 2);
    const zoomRatio = newZoom / oldZoom;

    const nextPanX = cursorX - ((cursorX - previewPan.x) * zoomRatio);
    const nextPanY = cursorY - ((cursorY - previewPan.y) * zoomRatio);

    setPreviewPan({ x: nextPanX, y: nextPanY });
    setPreviewZoom(newZoom);
  };

  const preventModalWheelScroll = (event) => {
    event.preventDefault();
  };

  const handlePdfLoadSuccess = ({ numPages }) => {
    const total = numPages || 0;
    setPdfPages(total);
    setPdfCurrentPage((prev) => {
      if (!total) return 1;
      if (prev < 1) return 1;
      if (prev > total) return total;
      return prev;
    });
    setPdfLoadError('');
  };

  const handlePdfLoadError = () => {
    setPdfLoadError('No se pudo cargar el PDF en el visor interno. Puedes descargarlo para abrirlo.');
  };

  const canPanPreview = Boolean(
    previewAdjunto
    && (previewAdjunto.isImage || previewAdjunto.isPdf)
    && previewZoom > 1
  );

  const iniciarArrastrePreview = (event) => {
    if (!canPanPreview) return;
    event.preventDefault();
    dragStartRef.current = {
      startPointerX: event.clientX,
      startPointerY: event.clientY,
      startPanX: previewPan.x,
      startPanY: previewPan.y
    };
    setIsPreviewDragging(true);
  };

  useEffect(() => {
    if (!isPreviewDragging) return undefined;

    const moverPreview = (event) => {
      const deltaX = event.clientX - dragStartRef.current.startPointerX;
      const deltaY = event.clientY - dragStartRef.current.startPointerY;

      setPreviewPan({
        x: dragStartRef.current.startPanX + deltaX,
        y: dragStartRef.current.startPanY + deltaY
      });
    };

    const finalizarArrastre = () => {
      setIsPreviewDragging(false);
    };

    window.addEventListener('mousemove', moverPreview);
    window.addEventListener('mouseup', finalizarArrastre);

    return () => {
      window.removeEventListener('mousemove', moverPreview);
      window.removeEventListener('mouseup', finalizarArrastre);
    };
  }, [isPreviewDragging, previewAdjunto]);

  useEffect(() => {
    if (previewZoom <= 1 && (previewPan.x !== 0 || previewPan.y !== 0)) {
      setPreviewPan({ x: 0, y: 0 });
    }
  }, [previewZoom, previewPan.x, previewPan.y]);

  useEffect(() => {
    if (!previewAdjunto?.isPdf) {
      setPdfRenderScale(1);
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      setPdfRenderScale(previewZoom);
    }, 140);

    return () => clearTimeout(timeoutId);
  }, [previewAdjunto?.isPdf, previewZoom]);

  const irPaginaPdfAnterior = () => {
    setPdfCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const irPaginaPdfSiguiente = () => {
    setPdfCurrentPage((prev) => Math.min(prev + 1, Math.max(pdfPages, 1)));
  };

  const pdfVisualScale = previewAdjunto?.isPdf
    ? (previewZoom / Math.max(pdfRenderScale, 0.01))
    : 1;

  const renderAdjuntoPreview = (adjunto) => {
    if (isImageAttachment(adjunto)) {
      return (
        <img
          className="notificacion-adjuntos__preview-image"
          src={buildAdjuntoUrl(adjunto.id, 'view')}
          alt={adjunto.file_name || 'Adjunto'}
          loading="lazy"
        />
      );
    }

    if (isPdfAttachment(adjunto)) {
      const displayName = normalizeDisplayFileName(adjunto.file_name);
      return (
        <span className="notificacion-adjuntos__preview-pdf-card" title={displayName || 'PDF'}>
          <span className="notificacion-adjuntos__preview-pdf-label">PDF</span>
          <small>Vista previa</small>
        </span>
      );
    }

    return (
      <span className="notificacion-adjuntos__ext-badge">
        {getFileExtension(adjunto.file_name)}
      </span>
    );
  };

  const renderAdjuntos = (adjuntos) => {
    if (!Array.isArray(adjuntos) || !adjuntos.length) return null;
    return (
      <div className="notificacion-adjuntos">
        <strong>Adjuntos ({adjuntos.length})</strong>
        <ul className="notificacion-adjuntos__list">
          {adjuntos.map((adjunto) => (
            <li key={adjunto.id || `${adjunto.file_name}-${adjunto.created_at}`}>
              {/** Normalize legacy filenames with mojibake in UI. */}
              {(() => {
                const displayName = normalizeDisplayFileName(adjunto.file_name);
                return (
              <button
                type="button"
                className="notificacion-adjuntos__preview-wrap"
                onClick={() => abrirPreviewAdjunto(adjunto)}
                aria-label={`Abrir vista previa de ${displayName || 'adjunto'}`}
              >
                {renderAdjuntoPreview(adjunto)}
              </button>
                );
              })()}
              <span className="notificacion-adjuntos__name">{normalizeDisplayFileName(adjunto.file_name)}</span>
              <span className="notificacion-adjuntos__meta">{formatFileSize(adjunto.file_size)}</span>
              <div className="notificacion-adjuntos__actions">
                <a href={buildAdjuntoUrl(adjunto.id, 'download')} target="_blank" rel="noreferrer">Descargar</a>
                <a href={buildAdjuntoUrl(adjunto.id, 'view')} target="_blank" rel="noreferrer">Ver</a>
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const marcarAtendida = async (destinatarioId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/notificaciones/destinatarios/${destinatarioId}/atendida`,
        { method: 'PATCH' }
      );
      if (!response.ok) {
        throw new Error('No se pudo actualizar el estado.');
      }
      cargarNotificaciones();
      cargarEnviadas();
    } catch (error) {
      console.error(error);
      setFeedback({ type: 'error', text: 'No se pudo marcar como atendida.' });
    }
  };

  return (
    <div className="notificaciones-page">
      <section className="notificaciones-page__pane notificaciones-page__pane--composer">
        <h2 className="notificaciones-title">Enviar notificación</h2>
        <p className="notificaciones-subtitle">
          Comparte indicaciones o avisos con uno o varios usuarios al mismo tiempo.
        </p>
        <div className="notificaciones-destinatarios">
          <div className="notificaciones-destinatarios__header">
            <span>Destinatarios ({destinatariosSeleccionados.length})</span>
            <button type="button" className="link-button" onClick={toggleSeleccionTodos}>
              {destinatariosSeleccionados.length === destinatariosDisponibles.length ? 'Quitar todos' : 'Seleccionar todos'}
            </button>
          </div>
          <div className="notificaciones-destinatarios__list">
            {loadingUsuarios && <div className="notificaciones-empty">Cargando usuarios…</div>}
            {!loadingUsuarios && !destinatariosDisponibles.length && (
              <div className="notificaciones-empty">No hay otros usuarios disponibles.</div>
            )}
            {!loadingUsuarios && destinatariosDisponibles.map((user) => (
              <label key={user.id} className="notificaciones-checkbox">
                <input
                  type="checkbox"
                  checked={destinatariosSeleccionados.includes(user.usuario)}
                  onChange={() => toggleDestinatario(user.usuario)}
                />
                <span>
                  {user.usuario}
                  <small>{user.rol}</small>
                </span>
              </label>
            ))}
          </div>
        </div>
        <div className="notificaciones-mensaje">
          <label>Mensaje</label>
          <textarea
            rows={4}
            value={mensaje}
            onChange={(event) => setMensaje(event.target.value)}
            placeholder="Describe la indicación o aviso que quieres compartir"
          />
        </div>
        <div className="notificaciones-adjuntos-uploader">
          <label htmlFor="notificaciones-adjuntos">Adjuntar archivos (cualquier formato)</label>
          <input
            id="notificaciones-adjuntos"
            type="file"
            multiple
            onChange={handleAdjuntosChange}
          />
          {!!archivosAdjuntos.length && (
            <ul className="notificaciones-adjuntos-uploader__list">
              {archivosAdjuntos.map((archivo, index) => (
                <li key={`${archivo.name}-${archivo.size}-${index}`}>
                  <span>{archivo.name} ({formatFileSize(archivo.size)})</span>
                  <button type="button" onClick={() => quitarAdjunto(index)}>Quitar</button>
                </li>
              ))}
            </ul>
          )}
        </div>
        {feedback.text && (
          <div className={`notificaciones-feedback notificaciones-feedback--${feedback.type}`}>
            {feedback.text}
          </div>
        )}
        <button
          type="button"
          className="notificaciones-send"
          onClick={handleEnviar}
          disabled={sending}
        >
          {sending ? 'Enviando…' : 'Enviar notificación'}
        </button>
      </section>

      <section className="notificaciones-page__pane notificaciones-page__pane--inbox">
        <div className="notificaciones-header">
          <div>
            <h2 className="notificaciones-title">Bandeja de notificaciones</h2>
            <p className="notificaciones-subtitle">
              Marca como atendidas cuando hayas revisado el mensaje.
            </p>
          </div>
          <div className="notificaciones-filtros">
            {estadoFilters.map((filter) => (
              <button
                key={filter.value}
                type="button"
                className={`pill-button${filtroEstado === filter.value ? ' pill-button--active' : ''}`}
                onClick={() => setFiltroEstado(filter.value)}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>
        <div className="notificaciones-lista">
          {loadingLista && <div className="notificaciones-empty">Cargando notificaciones…</div>}
          {!loadingLista && !notificaciones.length && (
            <div className="notificaciones-empty">No hay notificaciones que mostrar.</div>
          )}
          {!loadingLista && notificaciones.map((item) => {
            const isFocused = focusScope === 'inbox' && focusId === item.notificacionId;
            return (
              <article
                key={item.id}
                ref={(node) => {
                  if (node) {
                    inboxRefs.current[item.notificacionId] = node;
                  } else {
                    delete inboxRefs.current[item.notificacionId];
                  }
                }}
                className={`notificacion-card notificacion-card--${item.estado}${isFocused ? ' notificacion-card--focused' : ''}`}
              >
                <header>
                  <div>
                    <strong>{item.remitente}</strong>
                    <span className="notificacion-date">{formatDate(item.creadoEn)}</span>
                  </div>
                  <span className={`status-chip status-chip--${item.estado}`}>
                    {item.estado === 'atendida' ? 'Atendida' : 'Pendiente'}
                  </span>
                </header>
                <p>{item.mensaje}</p>
                {renderAdjuntos(item.adjuntos)}
                <footer>
                  {item.estado === 'pendiente' && (
                    <button
                      type="button"
                      className="mark-button"
                      onClick={() => marcarAtendida(item.id)}
                    >
                      Marcar como atendida
                    </button>
                  )}
                  {item.estado === 'atendida' && item.atendidoEn && (
                    <small>Atendida el {formatDate(item.atendidoEn)}</small>
                  )}
                </footer>
              </article>
            );
          })}
        </div>
      </section>

        <section className="notificaciones-page__pane notificaciones-page__pane--full notificaciones-page__pane--sent">
          <div className="notificaciones-header">
            <div>
              <h2 className="notificaciones-title">Notificaciones enviadas</h2>
              <p className="notificaciones-subtitle">
                Consulta quién ya las atendió y haz seguimiento rápido.
              </p>
            </div>
          </div>
          <div className="notificaciones-lista">
            {loadingEnviadas && <div className="notificaciones-empty">Cargando historial…</div>}
            {!loadingEnviadas && !sentNotifications.length && (
              <div className="notificaciones-empty">Aún no has enviado notificaciones.</div>
            )}
            {!loadingEnviadas && sentNotifications.map((item) => {
              const isFocused = focusScope === 'sent' && focusId === item.id;
              return (
                <article
                  key={item.id}
                  ref={(node) => {
                    if (node) {
                      sentRefs.current[item.id] = node;
                    } else {
                      delete sentRefs.current[item.id];
                    }
                  }}
                  className={`notificacion-card notificacion-card--enviada${isFocused ? ' notificacion-card--focused' : ''}`}
                >
                  <header>
                    <div>
                      <strong>Enviada el {formatDate(item.creadoEn)}</strong>
                      <span className="notificacion-date">
                        Destinatarios: {item.destinatarios.join(', ') || '—'}
                      </span>
                    </div>
                    <span className="status-chip status-chip--enviada">
                      {item.atendidas}/{item.total} atendidas
                    </span>
                  </header>
                  <p>{item.mensaje}</p>
                  {renderAdjuntos(item.adjuntos)}
                  <footer>
                    <small className="notificacion-meta">
                      {item.atendidas === item.total
                        ? 'Todos confirmaron recepción.'
                        : `${item.pendientes.length === 1 ? 'Pendiente por atender' : 'Pendientes por atender'}: ${item.pendientes.join(', ') || 'Sin datos'}`}
                    </small>
                  </footer>
                </article>
              );
            })}
          </div>
        </section>
      {previewAdjunto && (
        <div
          className="notificacion-preview-modal"
          onClick={cerrarPreviewAdjunto}
          onWheel={preventModalWheelScroll}
          role="presentation"
        >
          <div
            className="notificacion-preview-modal__dialog"
            role="dialog"
            aria-modal="true"
            aria-label={previewAdjunto.fileName || 'Vista previa de adjunto'}
            onClick={(event) => event.stopPropagation()}
          >
            <header className="notificacion-preview-modal__header">
              <strong title={previewAdjunto.fileName}>{previewAdjunto.fileName}</strong>
              <div className="notificacion-preview-modal__actions">
                {(previewAdjunto.isImage || previewAdjunto.isPdf) && (
                  <>
                    <button
                      type="button"
                      className="notificacion-preview-modal__zoom"
                      onClick={zoomOutPreview}
                    >
                      -
                    </button>
                    <span className="notificacion-preview-modal__zoom-value">
                      {Math.round(previewZoom * 100)}%
                    </span>
                    <button
                      type="button"
                      className="notificacion-preview-modal__zoom"
                      onClick={zoomInPreview}
                    >
                      +
                    </button>
                    <button
                      type="button"
                      className="notificacion-preview-modal__zoom-reset"
                      onClick={resetPreviewZoom}
                    >
                      Reset
                    </button>
                    {previewAdjunto.isPdf && pdfPages > 1 && (
                      <>
                        <button
                          type="button"
                          className="notificacion-preview-modal__zoom-reset"
                          onClick={irPaginaPdfAnterior}
                          disabled={pdfCurrentPage <= 1}
                        >
                          Pagina -
                        </button>
                        <span className="notificacion-preview-modal__zoom-value">
                          {pdfCurrentPage}/{pdfPages}
                        </span>
                        <button
                          type="button"
                          className="notificacion-preview-modal__zoom-reset"
                          onClick={irPaginaPdfSiguiente}
                          disabled={pdfCurrentPage >= pdfPages}
                        >
                          Pagina +
                        </button>
                      </>
                    )}
                  </>
                )}
                <button
                  type="button"
                  className="notificacion-preview-modal__close"
                  onClick={cerrarPreviewAdjunto}
                >
                  Cerrar
                </button>
              </div>
            </header>
            <div className="notificacion-preview-modal__body">
              {previewAdjunto.isImage && (
                <div
                  ref={previewStageRef}
                  className={`notificacion-preview-modal__stage${canPanPreview ? ' notificacion-preview-modal__stage--pannable' : ''}${isPreviewDragging ? ' notificacion-preview-modal__stage--dragging' : ''}`}
                  onMouseDown={iniciarArrastrePreview}
                  onWheel={handlePreviewWheel}
                  role="presentation"
                >
                  <img
                    className="notificacion-preview-modal__image"
                    src={previewAdjunto.viewUrl}
                    alt={previewAdjunto.fileName || 'Adjunto'}
                    style={{ transform: `translate(${previewPan.x}px, ${previewPan.y}px) scale(${previewZoom})` }}
                  />
                </div>
              )}
              {previewAdjunto.isPdf && (
                <div
                  ref={previewStageRef}
                  className={`notificacion-preview-modal__stage notificacion-preview-modal__stage--pdfjs${canPanPreview ? ' notificacion-preview-modal__stage--pannable' : ''}${isPreviewDragging ? ' notificacion-preview-modal__stage--dragging' : ''}`}
                  onMouseDown={iniciarArrastrePreview}
                  onWheel={handlePreviewWheel}
                  role="presentation"
                >
                  <Document
                    file={previewAdjunto.viewUrl}
                    onLoadSuccess={handlePdfLoadSuccess}
                    onLoadError={handlePdfLoadError}
                    loading={<div className="notificacion-preview-modal__pdf-loading">Cargando PDF...</div>}
                    error={<div className="notificacion-preview-modal__pdf-error">No se pudo renderizar el PDF.</div>}
                  >
                    <div className="notificacion-preview-modal__pdf-pages">
                    <div
                      className="notificacion-preview-modal__pdf-pan"
                      style={{
                        transform: `translate(${previewPan.x}px, ${previewPan.y}px) scale(${pdfVisualScale})`
                      }}
                    >
                      <Page
                        key={`pdf-page-${pdfCurrentPage}-${pdfRenderScale}`}
                        pageNumber={pdfCurrentPage}
                        scale={pdfRenderScale}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                      />
                    </div>
                    </div>
                  </Document>
                  {pdfLoadError && <div className="notificacion-preview-modal__pdf-error">{pdfLoadError}</div>}
                </div>
              )}
              {!previewAdjunto.isImage && !previewAdjunto.isPdf && (
                <div className="notificacion-preview-modal__fallback">
                  <span className="notificacion-adjuntos__ext-badge">
                    {getFileExtension(previewAdjunto.fileName)}
                  </span>
                  <p>Este tipo de archivo no tiene previsualización interna.</p>
                </div>
              )}
            </div>
            <footer className="notificacion-preview-modal__footer">
              <a href={previewAdjunto.downloadUrl} target="_blank" rel="noreferrer">Descargar archivo</a>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificacionesPage;
